import type { ActionOutput, OsFamily } from '@occ/contracts';
import { post } from '../api/http';
import { ROUTES } from '../api/routes';
import { runAction } from './actions';

/**
 * Navegador de archivos remoto (Módulo 6.3, RF-11/RF-12).
 *
 * Todo pasa por el catálogo de acciones, igual que el resto: `files.list` para
 * recorrer, `files.download`/`files.upload` para mover bytes y `files.delete`
 * para borrar. El panel no habla con el nodo ni con el almacén de staging por su
 * cuenta — salvo el `PUT` de los bytes, que va a la URL firmada que el propio
 * backend le entrega.
 *
 * Las cuatro acciones están implementadas en el backend. Lo que falta es una sola
 * pieza y solo para **subir desde el disco del operador**: el backend llena su
 * staging cuando él baja un archivo de un nodo, pero no expone ninguna ruta que
 * dé al navegador una URL firmada donde dejar los suyos. Ver `ROUTES.fileStaging`.
 */

export type FileEntry = ActionOutput<'files.list'>['entries'][number];

// ── Rutas: el nodo puede ser Windows o no, y el panel no ────────────────────

export type PathStyle = 'windows' | 'posix';

/**
 * Estilo de ruta del nodo.
 *
 * Se deriva del `osFamily` del inventario y no se adivina de la primera ruta que
 * conteste el nodo: un nodo recién enrolado todavía no reportó inventario, y en
 * ese caso POSIX es la apuesta menos dañina (una barra de más en Windows la
 * tolera la API de Win32; una contrabarra en Linux es un nombre de archivo).
 */
export const pathStyleFor = (osFamily: OsFamily | null | undefined): PathStyle =>
  osFamily === 'windows' ? 'windows' : 'posix';

export const rootOf = (style: PathStyle): string => (style === 'windows' ? 'C:\\' : '/');

const separatorOf = (style: PathStyle): string => (style === 'windows' ? '\\' : '/');

/** Normaliza separadores para poder partir la ruta sin preguntar de dónde viene. */
const normalize = (path: string): string => path.replace(/\\/g, '/');

export const joinPath = (style: PathStyle, base: string, name: string): string => {
  const separator = separatorOf(style);
  const trimmed = base.endsWith(separator) || base.endsWith('/') ? base.slice(0, -1) : base;
  return `${trimmed}${separator}${name}`;
};

export const nameOf = (path: string): string => {
  const parts = normalize(path).split('/').filter(Boolean);
  return parts.at(-1) ?? path;
};

/** Migas de pan: cada tramo con la ruta absoluta que le corresponde. */
export const breadcrumbOf = (
  style: PathStyle,
  path: string,
): Array<{ label: string; path: string }> => {
  const separator = separatorOf(style);
  const parts = normalize(path).split('/').filter(Boolean);

  if (style === 'windows') {
    const [drive, ...rest] = parts;
    if (drive === undefined) return [{ label: rootOf(style), path: rootOf(style) }];
    const crumbs = [{ label: `${drive}${separator}`, path: `${drive}${separator}` }];
    let current = `${drive}${separator}`;
    for (const part of rest) {
      current = joinPath(style, current, part);
      crumbs.push({ label: part, path: current });
    }
    return crumbs;
  }

  const crumbs = [{ label: '/', path: '/' }];
  let current = '';
  for (const part of parts) {
    current = `${current}/${part}`;
    crumbs.push({ label: part, path: current });
  }
  return crumbs;
};

export const parentOf = (style: PathStyle, path: string): string | null => {
  const crumbs = breadcrumbOf(style, path);
  return crumbs.length > 1 ? (crumbs.at(-2)?.path ?? null) : null;
};

// ── Lectura y borrado: acciones del catálogo, sin más ────────────────────────

export const listDirectory = async (nodeId: string, path: string): Promise<FileEntry[]> => {
  const { entries } = await runAction('files.list', { node: { nodeId }, path });
  // Directorios primero y por nombre: es el orden con el que se navega, y
  // ordenar en el panel evita depender de que cada sistema devuelva lo mismo.
  return [...entries].sort((left, right) => {
    const leftDirectory = left.kind === 'directory';
    const rightDirectory = right.kind === 'directory';
    if (leftDirectory !== rightDirectory) return leftDirectory ? -1 : 1;
    return left.name.localeCompare(right.name, 'es');
  });
};

export const deleteEntry = async (
  nodeId: string,
  path: string,
  recursive: boolean,
): Promise<void> => {
  await runAction('files.delete', { node: { nodeId }, path, recursive });
};

// ── Descarga ────────────────────────────────────────────────────────────────

export type TransferProgress = { transferredBytes: number; totalBytes: number | null };

const saveBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  // Sin revocar, el blob se queda en memoria hasta recargar la pestaña; con un
  // archivo de un giga eso se nota.
  URL.revokeObjectURL(url);
};

/**
 * Descarga un archivo del nodo.
 *
 * El backend responde con una URL de staging con expiración, y el panel la lee
 * en trozos para poder informar del progreso: un `window.open` daría la barra del
 * navegador, pero no dentro de la pantalla donde el técnico está trabajando. Si
 * la lectura por trozos falla —CORS del almacén, típicamente— se cae a abrir la
 * URL directamente, que es peor experiencia pero funciona.
 */
export const downloadFile = async (
  nodeId: string,
  path: string,
  onProgress: (progress: TransferProgress) => void,
): Promise<{ streamed: boolean; sizeBytes: number }> => {
  const ticket = await runAction('files.download', { node: { nodeId }, path });
  const fileName = nameOf(path);

  try {
    const response = await fetch(ticket.downloadUrl);
    if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

    const declared = Number(response.headers.get('Content-Length'));
    const totalBytes = Number.isFinite(declared) && declared > 0 ? declared : ticket.sizeBytes;

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let transferredBytes = 0;

    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      transferredBytes += value.byteLength;
      onProgress({ transferredBytes, totalBytes });
    }

    saveBlob(new Blob(chunks as BlobPart[]), fileName);
    return { streamed: true, sizeBytes: ticket.sizeBytes };
  } catch {
    window.open(ticket.downloadUrl, '_blank', 'noopener');
    return { streamed: false, sizeBytes: ticket.sizeBytes };
  }
};

// ── Subida ──────────────────────────────────────────────────────────────────

/**
 * Vale de staging. **El backend todavía no lo emite.**
 *
 * `files.upload` del catálogo exige un `stagingKey` que apunte a un objeto ya
 * subido, así que alguien tiene que crearlo antes: eso es `POST /files/staging`.
 * Es la única pieza de la subida que no puede degradar a nada — sin sitio donde
 * dejar los bytes no hay nada que mandar al nodo — y por eso el navegador de
 * archivos lo dice antes de pedir el archivo, y no después.
 */
export type StagingTicket = {
  stagingKey: string;
  uploadUrl: string;
  method: 'PUT' | 'POST';
  headers: Record<string, string>;
};

const parseStagingTicket = (payload: unknown): StagingTicket => {
  const record = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<
    string,
    unknown
  >;
  if (typeof record.stagingKey !== 'string' || typeof record.uploadUrl !== 'string') {
    throw new Error('El backend no devolvió un vale de staging utilizable');
  }
  return {
    stagingKey: record.stagingKey,
    uploadUrl: record.uploadUrl,
    method: record.method === 'POST' ? 'POST' : 'PUT',
    headers:
      typeof record.headers === 'object' && record.headers !== null
        ? (record.headers as Record<string, string>)
        : {},
  };
};

/**
 * SHA-256 del archivo, calculado en el navegador.
 *
 * `files.upload` lo pide para que el nodo pueda verificar que lo que escribe es
 * lo que se subió. Calcularlo aquí y no en el backend significa que un archivo
 * corrompido en el camino navegador→staging también se detecta.
 */
export const sha256Of = async (file: File): Promise<string> => {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

/** `PUT` de los bytes con progreso. Es la única razón de usar XHR y no `fetch`. */
const putWithProgress = (
  ticket: StagingTicket,
  file: File,
  onProgress: (progress: TransferProgress) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(ticket.method, ticket.uploadUrl, true);
    for (const [key, value] of Object.entries(ticket.headers)) {
      request.setRequestHeader(key, value);
    }
    request.upload.onprogress = (event) => {
      onProgress({
        transferredBytes: event.loaded,
        totalBytes: event.lengthComputable ? event.total : file.size,
      });
    };
    request.onload = () =>
      request.status >= 200 && request.status < 300
        ? resolve()
        : reject(new Error(`El staging rechazó el archivo (HTTP ${request.status})`));
    request.onerror = () => reject(new Error('No se pudo subir el archivo al staging'));
    request.send(file);
  });
};

export const uploadFile = async (
  nodeId: string,
  destinationPath: string,
  file: File,
  overwrite: boolean,
  onProgress: (progress: TransferProgress) => void,
): Promise<{ bytesWritten: number }> => {
  const sha256 = await sha256Of(file);

  const ticket = parseStagingTicket(
    await post<unknown>(ROUTES.fileStaging, {
      fileName: file.name,
      sizeBytes: file.size,
      sha256,
    }),
  );

  await putWithProgress(ticket, file, onProgress);

  return runAction('files.upload', {
    node: { nodeId },
    destinationPath,
    stagingKey: ticket.stagingKey,
    sha256,
    overwrite,
  });
};
